import { useMemo, useState, type JSX } from 'react'
import type { CashPositionMonth } from '../api'
import type { CashMode } from '../cashMode'
import { monthLabel, yen } from '../format'
import { CHART_INK, POLARITY, SERIES } from '../charts/palette'
import { compactYen, domainFor, monthLabelY, plotValue, ticksFor, yFor } from '../charts/scale'

// Ephemeral scenario planning (Casey). Add hypothetical deals and expenses and
// watch the cash runway move — entirely in the browser. NOTHING is sent to the
// server or persisted: a scenario is a thought experiment, not a plan version
// (that is what the headcount editor is for). Reloading the page clears it.
//
// The scenario projects FORWARD from the month-end bank balance the cash-
// position line is anchored on: an item only moves months after that anchor,
// because a closed month's balance cannot be rewritten. The baseline follows
// the page's pipeline switch, so a hypothetical deal is added to the same line
// the cash-position chart shows (booked by default, so a deal added here is
// not double-counted against the pipeline it may already be part of).
//
// In 'month-end-actuals' mode (GTM-54) the base is the chart's: the real
// month-end balance for actual months, the projection from the latest actual
// for projected months, and months with no balance skipped. Items then move only
// months strictly after the projection's anchor (the latest actual).

type ScenarioKind = 'deal' | 'expense'
type ScenarioItem = { id: number; kind: ScenarioKind; label: string; amount: string; month: string }

const W = 720
const H = 274
const PAD = { top: 18, right: 16, bottom: 48, left: 62 }
const PLOT_W = W - PAD.left - PAD.right
const PLOT_H = H - PAD.top - PAD.bottom
const LABEL_Y = monthLabelY(PAD.top + PLOT_H)

const monthIso = (yyyymm: string): string => `${yyyymm}-01`
const signed = (item: ScenarioItem): number => {
  const magnitude = plotValue(item.amount) ?? 0
  return item.kind === 'deal' ? magnitude : -magnitude
}

// The month after a YYYY-MM-01 date, as a YYYY-MM string (the earliest month a
// scenario item can affect — the anchor month itself has closed).
const nextMonthOf = (iso: string): string => {
  const [y, m] = iso.split('-').map(Number)
  const d = new Date(Date.UTC(y ?? 0, m ?? 0, 1)) // m is 1-based, so this is the next month
  return d.toISOString().slice(0, 7)
}

// The month-end-actuals base for one month, or null when it has none (a month
// with no bank balance, or one with nothing to project from).
function monthEndBase(month: CashPositionMonth, showPipeline: boolean): string | null {
  if (month.pointKind === 'actual') return month.actualBalance
  if (month.pointKind === 'projected') {
    return showPipeline ? month.projectedBalance : month.projectedBalanceExPipeline
  }
  return null
}

export function ScenarioPlanner({
  base,
  showPipeline,
  mode = 'single-anchor',
}: {
  base: CashPositionMonth[]
  showPipeline: boolean
  mode?: CashMode
}): JSX.Element {
  const [items, setItems] = useState<ScenarioItem[]>([])
  const [nextId, setNextId] = useState(1)
  const [draft, setDraft] = useState<{ kind: ScenarioKind; label: string; amount: string; month: string }>({
    kind: 'deal',
    label: '',
    amount: '',
    month: '',
  })
  const [hover, setHover] = useState<number | null>(null)
  const [addError, setAddError] = useState<string | null>(null)

  const monthEnd = mode === 'month-end-actuals'
  const anchorMonth = monthEnd
    ? (base.find((month) => month.projectionAnchorMonth !== null)?.projectionAnchorMonth ?? null)
    : (base[0]?.anchorMonth ?? null)

  const rows = useMemo(() => {
    return base
      .map((month) => {
        const baseStr = monthEnd
          ? monthEndBase(month, showPipeline)
          : showPipeline
            ? month.cashPosition
            : month.cashPositionExPipeline
        if (baseStr === null) return null
        const basePos = plotValue(baseStr)
        if (basePos === null) return null
        // Scenario delta: signed sum of every item strictly after the anchor and
        // up to and including this month.
        const delta = items.reduce((acc, item) => {
          const iso = monthIso(item.month)
          if (anchorMonth !== null && iso > anchorMonth && iso <= month.month) return acc + signed(item)
          return acc
        }, 0)
        // Keep the original decimal string for the baseline (a real server
        // figure); scenario/delta have no server string and must be computed.
        return { month: month.month, base: basePos, baseStr, scenario: basePos + delta }
      })
      .filter((row): row is { month: string; base: number; baseStr: string; scenario: number } => row !== null)
  }, [base, items, anchorMonth, showPipeline, monthEnd])

  const addItem = (): void => {
    if (draft.month === '') {
      setAddError('Pick a month for this item.')
      return
    }
    if ((plotValue(draft.amount) ?? 0) === 0) {
      setAddError('Enter a non-zero amount.')
      return
    }
    setAddError(null)
    setItems((prev) => [...prev, { id: nextId, ...draft, label: draft.label.trim() || (draft.kind === 'deal' ? 'Deal' : 'Expense') }])
    setNextId((id) => id + 1)
    setDraft({ kind: draft.kind, label: '', amount: '', month: draft.month })
  }

  if (rows.length === 0) {
    return (
      <section className="panel">
        <h2>Scenario planning</h2>
        <p className="notice">
          A scenario projects off the running cash position, which needs a month-end bank balance to
          anchor on. The planner appears once one is on record.
        </p>
      </section>
    )
  }

  // An item only moves the line if it lands strictly after the month-end anchor
  // and within the projected window. Totals and the item chips honour the same
  // predicate, so the panel's numbers never disagree with the runway they
  // describe (an out-of-window item is shown, but marked inactive).
  const lastMonthIso = rows[rows.length - 1]?.month ?? null
  const affects = (item: ScenarioItem): boolean =>
    anchorMonth !== null &&
    lastMonthIso !== null &&
    monthIso(item.month) > anchorMonth &&
    monthIso(item.month) <= lastMonthIso
  const totalInflow = items.filter((i) => i.kind === 'deal' && affects(i)).reduce((a, i) => a + (plotValue(i.amount) ?? 0), 0)
  const totalOutflow = items.filter((i) => i.kind === 'expense' && affects(i)).reduce((a, i) => a + (plotValue(i.amount) ?? 0), 0)
  const minMonth = anchorMonth ? nextMonthOf(anchorMonth) : undefined
  const maxMonth = lastMonthIso ? lastMonthIso.slice(0, 7) : undefined
  const last = rows[rows.length - 1]
  const endingDelta = last ? last.scenario - last.base : 0

  const { min, max } = domainFor([...rows.map((r) => r.base), ...rows.map((r) => r.scenario)])
  const band = PLOT_W / rows.length
  const y = (value: number): number => yFor(value, min, max, PAD.top, PLOT_H)
  const centre = (index: number): number => PAD.left + band * index + band / 2
  const baseLine = rows.map((r, i) => `${centre(i)},${y(r.base)}`).join(' ')
  const scenarioLine = rows.map((r, i) => `${centre(i)},${y(r.scenario)}`).join(' ')
  const active = hover === null ? null : (rows[hover] ?? null)

  return (
    <section className="panel chart-panel">
      <header className="chart-head">
        <div>
          <h2>Scenario planning</h2>
          <p className="chart-sub">
            Add hypothetical deals and expenses to see how the cash runway moves. Nothing here is
            saved — it is a what-if, cleared on reload.
            {items.length > 0 && last ? (
              <>
                {' '}Ending cash {monthLabel(last.month)}: <b>{yen(String(last.scenario))}</b> vs{' '}
                {yen(last.baseStr)} baseline (<b>{endingDelta >= 0 ? '+' : ''}{yen(String(endingDelta))}</b>).
              </>
            ) : null}
          </p>
        </div>
        <ul className="legend">
          <li>
            <span className="swatch line" style={{ background: CHART_INK.tick }} /> Baseline
            {showPipeline ? ' (incl. pipeline)' : ' (booked)'}
          </li>
          <li>
            <span className="swatch line" style={{ background: SERIES.blue }} /> Scenario
          </li>
        </ul>
      </header>

      <div className="scenario-add">
        <select
          aria-label="Kind"
          value={draft.kind}
          onChange={(e) => setDraft({ ...draft, kind: e.target.value as ScenarioKind })}
        >
          <option value="deal">Deal (money in)</option>
          <option value="expense">Expense (money out)</option>
        </select>
        <input
          aria-label="Label"
          value={draft.label}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          placeholder="Label (e.g. Big client)"
        />
        <input
          aria-label="Amount"
          value={draft.amount}
          onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
          inputMode="decimal"
          placeholder="Amount"
        />
        <input
          aria-label="Month"
          type="month"
          min={minMonth}
          max={maxMonth}
          value={draft.month}
          onChange={(e) => setDraft({ ...draft, month: e.target.value })}
        />
        <button type="button" onClick={addItem}>
          Add
        </button>
        {addError !== null && (
          <span className="add-error" role="alert">
            {addError}
          </span>
        )}
      </div>

      {items.length > 0 && (
        <ul className="scenario-items">
          {items.map((item) => (
            <li
              key={item.id}
              className={`${item.kind}${affects(item) ? '' : ' inactive'}`}
              title={affects(item) ? undefined : 'Outside the projected window — this item does not move the runway.'}
            >
              <span className="tag">{item.kind === 'deal' ? '+' : '−'}</span>
              {item.label} · {yen(item.amount)} · {monthLabel(monthIso(item.month))}
              {!affects(item) && <span className="tag-note"> (outside window)</span>}
              <button type="button" className="row-remove" aria-label="Remove" onClick={() => setItems((p) => p.filter((i) => i.id !== item.id))}>
                ×
              </button>
            </li>
          ))}
          <li className="scenario-totals">
            {totalInflow > 0 && <span className="in">+{yen(String(totalInflow))} in</span>}
            {totalOutflow > 0 && <span className="out">−{yen(String(totalOutflow))} out</span>}
          </li>
        </ul>
      )}

      <div className="chart-wrap">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="chart"
          role="img"
          aria-label="Baseline cash position versus the scenario-adjusted cash position by month."
          onMouseLeave={() => setHover(null)}
        >
          {ticksFor(min, max).map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={W - PAD.right}
                y1={y(tick)}
                y2={y(tick)}
                stroke={Math.abs(tick) < 1e-9 ? CHART_INK.axis : CHART_INK.grid}
              />
              <text x={PAD.left - 8} y={y(tick) + 3} textAnchor="end" className="tick">
                {compactYen(tick)}
              </text>
            </g>
          ))}

          <polyline points={baseLine} fill="none" stroke={CHART_INK.tick} strokeWidth={1.5} strokeDasharray="4 3" strokeLinejoin="round" />
          <polyline points={scenarioLine} fill="none" stroke={SERIES.blue} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

          {rows.map((row, index) => (
            <circle key={`s-${row.month}`} cx={centre(index)} cy={y(row.scenario)} r={hover === index ? 5 : 2.5} fill={SERIES.blue} stroke="#272C47" strokeWidth={1.5} />
          ))}

          {rows.map((row, index) => (
            <text key={`x-${row.month}`} x={centre(index)} y={LABEL_Y} className="tick" textAnchor="end" transform={`rotate(-35 ${centre(index)} ${LABEL_Y})`}>
              {monthLabel(row.month)}
            </text>
          ))}

          {rows.map((row, index) => (
            <rect key={`hit-${row.month}`} x={PAD.left + band * index} y={PAD.top} width={band} height={PLOT_H} fill="transparent" onMouseEnter={() => setHover(index)} />
          ))}

          {hover !== null && (
            <line x1={centre(hover)} x2={centre(hover)} y1={PAD.top} y2={PAD.top + PLOT_H} stroke={CHART_INK.axis} strokeWidth={1} pointerEvents="none" />
          )}
        </svg>

        {hover !== null && active !== null && (
          <div className="tooltip" style={{ left: `${(centre(hover) / W) * 100}%` }}>
            <strong>{monthLabel(active.month)}</strong>
            <span>
              Scenario <b>{yen(String(active.scenario))}</b>
            </span>
            <span>
              Baseline <b>{yen(active.baseStr)}</b>
            </span>
            <span className={active.scenario - active.base >= 0 ? 'delta-pos' : 'delta-neg'}>
              Difference <b>{active.scenario - active.base >= 0 ? '+' : ''}{yen(String(active.scenario - active.base))}</b>
            </span>
          </div>
        )}
      </div>
      <p className="chart-foot">
        <span className="swatch" style={{ background: POLARITY.positive }} /> deals lift the line,{' '}
        <span className="swatch" style={{ background: POLARITY.negative }} /> expenses lower it, from
        their month onward. Scenarios move only months after the month-end anchor.
      </p>
    </section>
  )
}
